import React, { useEffect, useState, useCallback, useMemo, useRef, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Button, Paper, useTheme, Chip, Alert, Portal, Dialog } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import {
  PageContainer,
  BreadcrumbContainer,
  BreadcrumbLinkComponent,
  PageHeader,
} from 'app/components/StyledComponents';
import { Breadcrumbs } from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  PersonSearch as PersonSearchIcon,
} from '@mui/icons-material';
import ActionDropdownMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/actionDropdown/ActionDropdownMAIQ';
import { NurturingPreferencesModal } from './components/NurturingPreferencesModal';
import { PickCandidateConfirmationModal } from './components/PickCandidateConfirmationModal';
import { useNurturingCandidatePreferences } from './hooks/useNurturingCandidatePreferences';
import { usePickCandidateConfirmation } from './hooks/usePickCandidateConfirmation';
import RunLogsApiService from './apiService/runLogsApiService';
import DataGridMAIQ, { DataGridMAIQRef } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import ModalMAIQ from 'app/components/ModalMAIQ';
import DataGridTypes from 'types/DataGridTypes';
import LoadingIndicator from 'app/components/LoadingIndicator';
import { useRunLogs } from 'app/context/RunLogsContext';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import RunLogDetailsModal, { SimpleRunLogDetailsModal } from './components/RunLogDetailsModal';
import UploadModal from './components/UploadModal';
import { RunLog } from './types';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import { useQueryClient } from '@tanstack/react-query';
// import SocketTestComponent from 'app/components/SocketTestComponent';
import SocketMonitoringPanel from 'app/components/SocketMonitoringPanel';
// import RunLogSimulator from 'app/components/RunLogSimulator';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { EmailGenerationProgressCard } from 'app/NewAIAvatarLayout/features/MiaChat/components/EmailGenerationProgressCard';
// import SocketToastManager, { ToastNotification } from 'app/components/SocketToastManager';

/** React Query keys for the Run Logs grid (GET …/run-v2, optional dealer in key). */
const isRunV2RunLogsQueryKey = (queryKey: unknown): boolean => {
  if (!Array.isArray(queryKey) || queryKey[0] !== 'GET') {
    return false;
  }
  return String(queryKey[1] ?? '').includes('run-v2');
};
const RETRY_SUBSCRIPTION_GRACE_MS = 60_000;

function RunLogsPage() {
  const history = useHistory();
  const dispatch = useDispatch();
  const runLogsContext = useRunLogs();
  const selectedDealer = useSelector(selectDealerSelected);
  const [selectedRunLog, setSelectedRunLog] = useState<RunLog | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [dataGridKey, setDataGridKey] = useState<string>('0');
  const [activeRunLogIds, setActiveRunLogIds] = useState<string[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [datagridData, setDatagridData] = useState<any[]>([]);
  const [forceRefreshCount, setForceRefreshCount] = useState(0);
  const [isSocketPanelOpen, setIsSocketPanelOpen] = useState(false);
  const [isNurturingPreferencesModalOpen, setIsNurturingPreferencesModalOpen] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [showJobSocketDetailsModal, setShowJobSocketDetailsModal] = useState(false);
  // const [toastNotifications, setToastNotifications] = useState<ToastNotification[]>([]);
  const theme = useTheme();
  const queryClient = useQueryClient();

  // Ref to access the DataGridMAIQ component
  const dataGridRef = useRef<DataGridMAIQRef>(null);
  /** Ref mirror of activeRunLogIds — prevents the realTimeRunLogs effect from
   *  re-triggering itself whenever subscription state changes (avoids infinite loop). */
  const activeRunLogIdsRef = useRef<string[]>([]);
  const recentlyRetriedRunLogIdsRef = useRef<Map<string, number>>(new Map());
  const processedRealtimeEventKeyByRunIdRef = useRef<Map<string, string>>(new Map());

  // Destructure context values with fallbacks
  const {
    loading = false,
    fetchRunByRunId = async () => {},
    clearSelectedRunLog = () => {},
    selectedRunLog: contextSelectedRunLog = null,
    socketConnected = false,
    realTimeRunLogs = [],
    subscribeToRunLog = () => {},
    unsubscribeFromRunLog = () => {},
    subscribeToJob = () => {},
    unsubscribeFromJob = () => {},
    jobLifecycleMessages = [],
    clearJobLifecycleMessages = () => {},
    clearPersistentData = () => {},
    error: contextError = null
  } = runLogsContext || {};

  // Keep ref in sync with state so effects can read current value without depending on it
  useEffect(() => {
    activeRunLogIdsRef.current = activeRunLogIds;
  }, [activeRunLogIds]);

  // On leaving Run Logs page: unsubscribe sockets, clear persisted subs, drop run-v2 React Query cache (no stale grid on return)
  useEffect(() => {
    return () => {
      clearPersistentData();
      queryClient.removeQueries({
        predicate: q => isRunV2RunLogsQueryKey(q.queryKey),
      });
    };
  }, [clearPersistentData, queryClient]);

  // Combine errors
  const error = localError || contextError;

  // Memoized dealer ID to prevent unnecessary re-renders
  const dealerId = useMemo(() => selectedDealer?._id, [selectedDealer?._id]);

  const {
    loading: nurturingPrefsLoading,
    refreshLoading: nurturingRefreshLoading,
    error: nurturingPrefsError,
    rows: nurturingRows,
    headerRow: nurturingHeaderRow,
    ttl: nurturingTtl,
    soldWaitDays: nurturingSoldWaitDays,
    unsoldWaitDays: nurturingUnsoldWaitDays,
    batchSizeByRowKey: nurturingBatchSizes,
    updateBatchSize: updateNurturingBatchSize,
    isAllChecked: isNurturingAllChecked,
    onAllCheckedChange: onNurturingAllCheckedChange,
    saveLoading: nurturingSaveLoading,
    savePreferences: saveNurturingPreferences,
    canSavePreferences: canSaveNurturingPreferences,
    refreshData: refreshNurturingPreferences,
    dateFilterType: nurturingDateFilterType,
    setDateFilterType: setNurturingDateFilterType,
    startDate: nurturingStartDate,
    setStartDate: setNurturingStartDate,
    endDate: nurturingEndDate,
    setEndDate: setNurturingEndDate,
  } = useNurturingCandidatePreferences(dealerId ?? '', isNurturingPreferencesModalOpen);

  const handleSaveNurturingPreferences = useCallback(async () => {
    try {
      await saveNurturingPreferences();
      setIsNurturingPreferencesModalOpen(false);
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'success',
          message: 'Preferences saved successfully',
        }),
      );
    } catch (err: any) {
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: err?.message || 'Failed to save preferences',
        }),
      );
    }
  }, [saveNurturingPreferences, dispatch]);

  const handlePickSuccess = useCallback(() => {
    setDataGridKey(Date.now().toString());
  }, []);

  const subscribeToJobWithTracking = useCallback(
    (jobId: string) => {
      setActiveJobId(jobId);
      subscribeToJob(jobId);
    },
    [subscribeToJob],
  );

  const {
    isOpen: isPickCandidateModalOpen,
    handleOpen: handleOpenPickCandidateModal,
    handleClose: handleClosePickCandidateModal,
    handleConfirmPick,
    pickLoading: isPickCandidateLoading,
    preferencesLoading: pickCandidatePrefsLoading,
    preferencesRefreshLoading: pickCandidateRefreshLoading,
    preferencesError: pickCandidatePrefsError,
    rows: pickCandidateRows,
    headerRow: pickCandidateHeaderRow,
    ttl: pickCandidateTtl,
    soldWaitDays: pickCandidateSoldWaitDays,
    unsoldWaitDays: pickCandidateUnsoldWaitDays,
    batchSizeByRowKey: pickCandidateBatchSizes,
    updateBatchSize: updatePickCandidateBatchSize,
    refreshPreferences: refreshPickCandidatePreferences,
    dateFilterType: pickDateFilterType,
    setDateFilterType: setPickDateFilterType,
    startDate: pickStartDate,
    setStartDate: setPickStartDate,
    endDate: pickEndDate,
    setEndDate: setPickEndDate,
    isAllChecked: isPickAllChecked,
    onAllCheckedChange: onPickAllCheckedChange,
  } = usePickCandidateConfirmation(dealerId ?? '', handlePickSuccess, subscribeToJobWithTracking);

  // Backend sends queue_job_lifecycle with nested payload: data.message = { job_id, job_name, status, message, ... }
  const getJobPayload = useCallback((m: { data?: any }) => m?.data?.message ?? m?.data, []);

  const jobMessagesForCurrentJob = useMemo(
    () =>
      activeJobId
        ? jobLifecycleMessages.filter(m => getJobPayload(m)?.job_id === activeJobId)
        : [],
    [jobLifecycleMessages, activeJobId, getJobPayload],
  );
  const latestJobMessage = jobMessagesForCurrentJob.length > 0 ? jobMessagesForCurrentJob[jobMessagesForCurrentJob.length - 1] : null;
  const latestPayload = latestJobMessage ? getJobPayload(latestJobMessage) : null;
  const jobProgressOverride = latestPayload?.status === 'completed' ? 100 : undefined;
  const jobCurrentSection =
    latestPayload?.message != null
      ? { index: 0, type: 'status', main: String(latestPayload.message) }
      : { index: 0, type: 'status', main: 'Running...' };

  useEffect(() => {
    if (!activeJobId || !latestPayload?.status) return;
    if (latestPayload.status === 'completed' || latestPayload.status === 'failed') {
      // Close all related modals and the progress card immediately
      setShowJobSocketDetailsModal(false);
      handleClosePickCandidateModal();
      setActiveJobId(null);
      unsubscribeFromJob(activeJobId);
      clearJobLifecycleMessages();

      // Re-render run log data grid so new/completed run appears
      queryClient.removeQueries({
        queryKey: ['GET', `${DataGridTypes.API_VERSION_2}/run-v2`],
        exact: false,
      });
      setDataGridKey(Date.now().toString());
    }
  }, [activeJobId, latestPayload?.status, unsubscribeFromJob, clearJobLifecycleMessages, queryClient, handleClosePickCandidateModal]);

  // Handle real-time run log status updates with better error handling
  const handleRunLogStatusUpdate = useCallback(
    (runLogEvent: any) => {
      try {

        // Validate runLogEvent
        if (!runLogEvent || typeof runLogEvent !== 'object') {
          return;
        }

        // Handle nested message structure from backend (as shown in the WebSocket messages)
        let eventData = runLogEvent;
        if (runLogEvent.message && typeof runLogEvent.message === 'object') {
          // Backend sends data in nested message object
          eventData = runLogEvent.message;
        }

        const runLogId = eventData.run_log_id || eventData.runId || eventData.id;
        if (!runLogId) {
          return;
        }

        const status = eventData.status || 'unknown';
        const message = eventData.message || 'No message available';


        // Prepare updates object for manual update
        const updates = {
          status: status,
        };

        // Call manual update function from DataGridMAIQ ref
        if (dataGridRef.current) {
          dataGridRef.current.manualUpdateData(runLogId, updates);
        }

        // Update query cache for RUN_LOGS endpoint
        queryClient.setQueryData(
          ['GET', `${DataGridTypes.API_VERSION_2}/run-v2`, { dealer: dealerId }],
          (oldData: any) => {
            if (!oldData?.body?.data) return oldData;
            const updated = { ...oldData, body: { ...oldData.body, data: [...oldData.body.data] } };
            const idx = updated.body.data.findIndex((r: any) => r._id === runLogId);
            if (idx >= 0) {
              updated.body.data[idx] = {
                ...updated.body.data[idx],
                status,
                run_status: status,
                last_update: new Date().toISOString(),
                progress: eventData.progress || undefined,
                message,
                end_timestamp: eventData.end_timestamp || undefined,
                duration_ms: eventData.duration_ms || undefined,
                total_records: eventData.total_records || undefined,
                processed_records: eventData.processed_records || undefined,
              };
            }
            return updated;
          },
        );

        // Don't force invalidate queries for socket updates - let manual updates handle it
        // queryClient.invalidateQueries({
        //   queryKey: ['GET', `${DataGridTypes.API_VERSION_2}/run-v2`],
        //   exact: false
        // });

        // Don't force DataGrid re-render for socket updates - manual updates are sufficient
        // setForceRefreshCount(prev => prev + 1);

      } catch (error) {
        setLocalError('Failed to update run log status');
      }
          },
      [queryClient, dealerId],
    );

  // Subscribe to run log updates when dealer changes
  useEffect(() => {
    if (dealerId) {
      setDataGridKey(Date.now().toString());
    }
  }, [dealerId]);

  // Subscribe to real-time run log events
  useEffect(() => {
    if (socketConnected && dealerId) {
    }
  }, [socketConnected, dealerId]);

  // Refresh datagrid when upload modal closes - REMOVED to prevent double refresh
  // The refresh is now handled only in handleUploadSuccess and handleCloseUploadModal

  // Handle real-time run log status updates from context: only update grid (setQueryData).
  // Subscribe/unsubscribe is handled by the query-cache subscriber below so we don't double-trigger.
  useEffect(() => {
    try {
      if (!Array.isArray(realTimeRunLogs)) return;
      if (realTimeRunLogs.length === 0) return;
      const runLog = realTimeRunLogs[realTimeRunLogs.length - 1];
      if (!runLog || typeof runLog !== 'object') return;
      const runLogId = runLog.run_log_id || runLog.runId || runLog.id;
      if (!runLogId) return;
      const eventKey = String(
        runLog.updated_at ??
          runLog.timestamp ??
          `${runLog.status ?? ''}|${runLog.message ?? ''}|${runLog.end_timestamp ?? ''}|${runLog.progress ?? ''}`,
      );
      const lastEventKey = processedRealtimeEventKeyByRunIdRef.current.get(String(runLogId));
      if (lastEventKey === eventKey) return;
      processedRealtimeEventKeyByRunIdRef.current.set(String(runLogId), eventKey);
      handleRunLogStatusUpdate(runLog);
    } catch (error) {
      setLocalError('Failed to process real-time updates');
    }
  }, [realTimeRunLogs, handleRunLogStatusUpdate]);

  /**
   * When run-v2 data loads or refreshes, subscribe to socket for all rows
   * whose status is not completed/failed, and unsubscribe for those that are done.
   * Ref is updated synchronously so rapid cache updates don't cause duplicate subscribe/unsubscribe.
   */
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(event => {
      if (
        event.type === 'updated' &&
        Array.isArray(event.query.queryKey) &&
        event.query.queryKey[0] === 'GET' &&
        String(event.query.queryKey[1]).includes('run-v2') &&
        event.query.state.status === 'success'
      ) {
        const responseData = event.query.state.data as any;
        const rows: any[] = responseData?.body?.data ?? [];
        if (!Array.isArray(rows) || rows.length === 0) return;
        const now = Date.now();
        recentlyRetriedRunLogIdsRef.current.forEach((retriedAt, id) => {
          if (now - retriedAt > RETRY_SUBSCRIPTION_GRACE_MS) {
            recentlyRetriedRunLogIdsRef.current.delete(id);
          }
        });
        const ref = activeRunLogIdsRef;
        let refChanged = false;
        rows.forEach((row: any) => {
          if (!row._id) return;
          const rowStatus = String(row.status ?? '').toLowerCase();
          const isDone = rowStatus === 'completed' || rowStatus === 'failed';
          const retriedAt = recentlyRetriedRunLogIdsRef.current.get(row._id);
          const isInRetryGrace = typeof retriedAt === 'number' && now - retriedAt <= RETRY_SUBSCRIPTION_GRACE_MS;
          if (isDone) {
            if (isInRetryGrace) {
              return;
            }
            if (ref.current.includes(row._id)) {
              ref.current = ref.current.filter(id => id !== row._id);
              refChanged = true;
              unsubscribeFromRunLog(row._id);
            }
          } else {
            if (!ref.current.includes(row._id)) {
              ref.current = [...ref.current, row._id];
              refChanged = true;
              subscribeToRunLog(row._id);
            }
          }
        });
        if (refChanged) setActiveRunLogIds([...ref.current]);
      }
    });
    return () => unsubscribe();
  }, [queryClient, subscribeToRunLog, unsubscribeFromRunLog]);

  // This is the ONLY place where datagrid refresh happens after upload
  // Called by UploadModal's onUploadSuccess callback after successful upload
  const handleUploadSuccess = useCallback(() => {
    try {
      queryClient.removeQueries({
        queryKey: ['GET', `${DataGridTypes.API_VERSION_2}/run-v2`],
        exact: false,
      });
      setDataGridKey(Date.now().toString());
    } catch (error) {
      setLocalError('Failed to refresh data after upload');
    }
  }, [queryClient]);

  // This only closes the modal - no refresh needed here
  // Refresh is handled by handleUploadSuccess when upload actually completes
  const handleCloseUploadModal = useCallback(() => {
    setIsUploadModalOpen(false);
  }, []);

  const handleCellClick = useCallback(async (cell: any, clickedElement?: string) => {
    try {
      if (!cell || !cell.field || !cell.row) {
        return;
      }

      if (cell.field === 'status' && clickedElement === 'retry') {
        const runLogId = cell?.row?._id;
        if (!runLogId) {
          setLocalError('Run log ID is missing');
          return;
        }
        if (!dealerId) {
          setLocalError('Please select a dealer first');
          return;
        }

        recentlyRetriedRunLogIdsRef.current.set(runLogId, Date.now());

        // Immediate UX feedback for the row while retry API is in-flight.
        dataGridRef.current?.manualUpdateData(runLogId, { __retrying: true });

        const retryResponse = await RunLogsApiService.retryRunLog(runLogId, dealerId);
        if (!retryResponse || retryResponse.body == null) {
          throw new Error(retryResponse?.message || 'Retry failed');
        }

        const returnedJobId =
          retryResponse?.body?.job_id ||
          retryResponse?.body?.data?.job_id ||
          retryResponse?.body?.message?.job_id;

        subscribeToRunLog(runLogId);
        if (returnedJobId) {
          subscribeToJob(String(returnedJobId));
        }

        dataGridRef.current?.manualUpdateData(runLogId, {
          __retrying: false,
          status: 'pending',
          run_status: 'pending',
        });

        queryClient.setQueryData(
          ['GET', `${DataGridTypes.API_VERSION_2}/run-v2`, { dealer: dealerId }],
          (oldData: any) => {
            if (!oldData?.body?.data || !Array.isArray(oldData.body.data)) {
              return oldData;
            }
            const updatedData = oldData.body.data.map((row: any) =>
              row?._id === runLogId
                ? {
                    ...row,
                    __retrying: false,
                    status: 'pending',
                    run_status: 'pending',
                    last_update: new Date().toISOString(),
                  }
                : row,
            );
            return {
              ...oldData,
              body: {
                ...oldData.body,
                data: updatedData,
              },
            };
          },
        );

        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'success',
            message: 'Retry started successfully',
          }),
        );
        return;
      }

      if (cell.field === '_id') {
        
        // Validate dealer ID
        if (!dealerId) {
          setLocalError('Please select a dealer first');
          return;
        }

        // Use the context to fetch detailed run log data
        fetchRunByRunId(cell.row._id, dealerId);
        setIsModalOpen(true);
      }
    } catch (error) {
      if (cell?.row?._id) {
        dataGridRef.current?.manualUpdateData(cell.row._id, { __retrying: false });
      }
      setLocalError(error instanceof Error ? error.message : 'Failed to process run log action');
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: error instanceof Error ? error.message : 'Failed to retry run log',
        }),
      );
    }
  }, [fetchRunByRunId, dealerId, dispatch, queryClient, subscribeToRunLog, subscribeToJob]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    clearSelectedRunLog();
  }, [clearSelectedRunLog]);

  const handleOpenUploadModal = useCallback(() => {
    setIsUploadModalOpen(true);
  }, []);

  const handleOpenSocketPanel = useCallback(() => {
    setIsSocketPanelOpen(true);
  }, [isSocketPanelOpen]);

  const handleCloseSocketPanel = useCallback(() => {
    setIsSocketPanelOpen(false);
  }, []);

  // Clear local error when context error changes
  useEffect(() => {
    if (contextError) {
      setLocalError(null);
    }
  }, [contextError]);

  // If no dealer is selected, show a message
  if (!selectedDealer || !dealerId) {
    return (
      <>
        <Helmet>
          <title>Run Logs</title>
          <meta name="description" content="Run Logs - Myautoiq" />
        </Helmet>
        <PageContainer>
          <Typography
            variant="h4"
            gutterBottom
            component="div"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              fontWeight: 'normal',
            }}
          >
            Please select a dealer
          </Typography>
        </PageContainer>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Run Logs</title>
        <meta name="description" content="Run Logs - Myautoiq" />
      </Helmet>
      {loading && <LoadingIndicator />}
      <PageContainer>
        {/* Error Display */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            onClose={() => setLocalError(null)}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <BreadcrumbContainer>
            <Breadcrumbs
              aria-label="breadcrumb"
              separator={<NavigateNextIcon fontSize="small" />}
            >
              <BreadcrumbLinkComponent underline="hover" color="inherit">
                {selectedDealer?.name || 'Dealer'}
              </BreadcrumbLinkComponent>
              <BreadcrumbLinkComponent underline="hover" color="text.primary">
                Run Logs
              </BreadcrumbLinkComponent>
            </Breadcrumbs>
          </BreadcrumbContainer>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            borderRadius: 1.5,
            backgroundColor: 'background.paper',
            boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DirectionsRunIcon
              sx={{
                color: theme.palette.primary.main,
                fontSize: 28,
              }}
            />
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  color: theme.palette.primary.main,
                }}
              >
                Run Logs
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '0.85rem' }}
              >
                View and manage run logs for{' '}
                {selectedDealer?.name || 'selected dealer'}
              </Typography>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}
              >
                <Chip
                  label={
                    socketConnected
                      ? 'Real-time Connected'
                      : 'Real-time Disconnected'
                  }
                  color={socketConnected ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                  onClick={handleOpenSocketPanel}
                  sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                />
                {/* Debug info */}
                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                  Panel: {isSocketPanelOpen ? 'Open' : 'Closed'}
                </Typography>
                {activeRunLogIds.length > 0 && (
                  <Chip
                    label={`${activeRunLogIds.length} Active`}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                )}
                {realTimeRunLogs.length > 0 && (
                  <Chip
                    label={`${realTimeRunLogs.length} Updates`}
                    color="secondary"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <ActionDropdownMAIQ
              buttonLabel="Nurturing Candidate Picker"
              color="primary"
              ariaLabel="Nurturing candidate picker actions"
              actions={[
                {
                  id: 'pick-candidate',
                  label: 'Pick Candidates',
                  onClick: handleOpenPickCandidateModal,
                  icon: <PersonSearchIcon fontSize="small" />,
                },
                {
                  id: 'preferences',
                  label: 'Preferences',
                  onClick: () => setIsNurturingPreferencesModalOpen(true),
                  icon: <SettingsIcon fontSize="small" />,
                },
              ]}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenUploadModal}
              startIcon={<UploadIcon />}
              sx={{
                textTransform: 'none',
              }}
            >
              Upload Raw Data
            </Button>
          </Box>
        </Paper>

        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 1.5,
            boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <DataGridMAIQ
            ref={dataGridRef}
            type={DataGridTypes.RUN_LOGS(dealerId)}
            onCellClick={handleCellClick}
            key={`${dataGridKey}-${forceRefreshCount}`} // Force re-render only for upload/refresh, not socket updates
          />
        </Box>
      </PageContainer>

      {contextSelectedRunLog && (
        <ErrorBoundary>
          <SimpleRunLogDetailsModal
            open={isModalOpen}
            onClose={handleCloseModal}
            runLog={contextSelectedRunLog}
          />
        </ErrorBoundary>
      )}

      <ErrorBoundary>
        <UploadModal
          open={isUploadModalOpen}
          onClose={handleCloseUploadModal}
          onUploadSuccess={handleUploadSuccess}
        />
      </ErrorBoundary>

      <NurturingPreferencesModal
        open={isNurturingPreferencesModalOpen}
        onClose={() => setIsNurturingPreferencesModalOpen(false)}
        loading={nurturingPrefsLoading}
        refreshLoading={nurturingRefreshLoading}
        error={nurturingPrefsError}
        rows={nurturingRows}
        headerRow={nurturingHeaderRow}
        ttl={nurturingTtl}
        soldWaitDays={nurturingSoldWaitDays}
        unsoldWaitDays={nurturingUnsoldWaitDays}
        batchSizeByRowKey={nurturingBatchSizes}
        onUpdateBatchSize={updateNurturingBatchSize}
        isAllChecked={isNurturingAllChecked}
        onAllCheckedChange={onNurturingAllCheckedChange}
        dateFilterType={nurturingDateFilterType}
        onDateFilterTypeChange={setNurturingDateFilterType}
        startDate={nurturingStartDate}
        onStartDateChange={setNurturingStartDate}
        endDate={nurturingEndDate}
        onEndDateChange={setNurturingEndDate}
        saveLoading={nurturingSaveLoading}
        canSavePreferences={canSaveNurturingPreferences}
        onSavePreferences={handleSaveNurturingPreferences}
        onRefresh={refreshNurturingPreferences}
      />

      <PickCandidateConfirmationModal
        open={isPickCandidateModalOpen}
        onClose={handleClosePickCandidateModal}
        loading={pickCandidatePrefsLoading}
        refreshLoading={pickCandidateRefreshLoading}
        error={pickCandidatePrefsError}
        rows={pickCandidateRows}
        headerRow={pickCandidateHeaderRow}
        ttl={pickCandidateTtl}
        soldWaitDays={pickCandidateSoldWaitDays}
        unsoldWaitDays={pickCandidateUnsoldWaitDays}
        batchSizeByRowKey={pickCandidateBatchSizes}
        onUpdateBatchSize={updatePickCandidateBatchSize}
        pickLoading={isPickCandidateLoading}
        onConfirmPick={handleConfirmPick}
        onRefresh={refreshPickCandidatePreferences}
        dateFilterType={pickDateFilterType}
        onDateFilterTypeChange={setPickDateFilterType}
        startDate={pickStartDate}
        onStartDateChange={setPickStartDate}
        endDate={pickEndDate}
        onEndDateChange={setPickEndDate}
        isAllChecked={isPickAllChecked}
        onAllCheckedChange={onPickAllCheckedChange}
      />

      {/* Job progress card (NURTURING_CANDIDATES_PICKER) – same pattern as EmailsGridPage */}
      {activeJobId && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1300,
              minWidth: 520,
            }}
          >
            <EmailGenerationProgressCard
              isGeneratingEmail={true}
              title="Nurturing candidates"
              currentSection={jobCurrentSection}
              progressOverride={jobProgressOverride}
              onOpenEmailGenerationPopup={() => setShowJobSocketDetailsModal(true)}
            />
          </Box>
        </Portal>
      )}

      {/* Job socket details modal – opened when user clicks progress card */}
      <Dialog
        open={showJobSocketDetailsModal}
        onClose={() => setShowJobSocketDetailsModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            maxHeight: 480,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
            Nurturing candidates picker
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              minHeight: 100,
            }}
          >
            {latestPayload ? (
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                  {String(latestJobMessage?.eventName ?? 'queue_job_lifecycle').replace(/_/g, ' ')}
                </Typography>
                {latestPayload.status != null && (
                  <Typography variant="body2" color="text.secondary">
                    Status: {String(latestPayload.status)}
                  </Typography>
                )}
                {latestPayload.message != null && (
                  <Typography variant="body2" color="text.secondary">
                    {String(latestPayload.message)}
                  </Typography>
                )}
                {latestPayload.duration_ms != null && (
                  <Typography variant="body2" color="text.secondary">
                    Duration: {Number(latestPayload.duration_ms)} ms
                  </Typography>
                )}
                {latestPayload.job_name != null && (
                  <Typography variant="body2" color="text.secondary">
                    Job: {String(latestPayload.job_name)}
                  </Typography>
                )}
                {latestPayload.timestamp != null && (
                  <Typography variant="body2" color="text.secondary">
                    Time: {String(latestPayload.timestamp)}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Waiting for job updates...
              </Typography>
            )}
          </Box>
          {jobMessagesForCurrentJob.length > 1 && (
            <Box sx={{ mt: 2, overflow: 'auto', maxHeight: 200 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Recent messages ({jobMessagesForCurrentJob.length})
              </Typography>
              {jobMessagesForCurrentJob.slice(-10).reverse().map((msg, i) => {
                const p = getJobPayload(msg);
                return (
                  <Box
                    key={`${msg.timestamp}-${i}`}
                    sx={{
                      py: 0.75,
                      px: 1,
                      mb: 0.5,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" fontWeight={600}>
                      {String(p?.status ?? msg.eventName)}
                    </Typography>
                    {p?.message != null && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {String(p.message)}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      </Dialog>

      {/* Socket Monitoring Panel Popup */}
      <ErrorBoundary>
        <ModalMAIQ
          open={isSocketPanelOpen}
          onClose={handleCloseSocketPanel}
          hideDefaultCloseButton={false}
        >
          <Box
            sx={{
              width: '90vw',
              maxWidth: '1200px',
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                borderRadius: 0,
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#0F2A52',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                <Typography variant="h6" fontWeight="600">
                  Socket Monitoring Panel
                </Typography>
                <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 400, fontSize: '0.8rem' }}>
                  Real-time socket connection and run log monitoring
                </Typography>
              </Box>

              {/* Content */}
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: 2,
                }}
              >
                <SocketMonitoringPanel />
              </Box>
            </Paper>
          </Box>
        </ModalMAIQ>
      </ErrorBoundary>
    </>
  );
}

export default RunLogsPage;
